// Simple global media manager using custom events
class MediaManager {
  constructor() {
    this.players = new Map();
    this.activePlayer = null;
    this.observer = null;
    this.init();
  }

  init() {
    // Auto-detect and manage all audio/video elements
    this.setupDOMWatcher();
    this.setupGlobalMediaListeners();
  }

  setupDOMWatcher() {
    // Watch for new audio/video elements being added/removed from DOM
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Handle added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the node itself is audio/video
            if (node.tagName === 'AUDIO' || node.tagName === 'VIDEO') {
              this.autoRegisterElement(node);
            }
            // Check for audio/video elements within the added node
            const mediaElements = node.querySelectorAll?.('audio, video') || [];
            mediaElements.forEach((element) => this.autoRegisterElement(element));
          }
        });
        
        // Handle removed nodes
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the node itself is audio/video
            if (node.tagName === 'AUDIO' || node.tagName === 'VIDEO' && node.dataset.mediaId) {
              this.unregisterPlayer(node.dataset.mediaId);
            }
            // Check for audio/video elements within the removed node
            const mediaElements = node.querySelectorAll?.('audio, video') || [];
            mediaElements.forEach((element) => {
              if (element.dataset.mediaId) {
                this.unregisterPlayer(element.dataset.mediaId);
              }
            });
          }
        });
      });
    });

    // Start observing
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Register existing elements when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('audio, video').forEach((element) => {
          this.autoRegisterElement(element);
        });
      });
    } else {
      document.querySelectorAll('audio, video').forEach((element) => {
        this.autoRegisterElement(element);
      });
    }
  }

  autoRegisterElement(element) {
    // Skip elements that are explicitly managed by their own context (e.g., AudioContext)
    if (element.dataset.mediaManual) {
      return;
    }

    // Skip if this element is already manually managed by checking if it's already registered
    // by iterating through existing players to see if this element is already managed
    for (const [existingId, player] of this.players) {
      if (player.element === element) {
        console.log(`Skipping auto-registration for element - already managed as: ${existingId}`);
        return;
      }
    }
    
    // Generate unique ID for unmanaged elements
    if (!element.dataset.mediaId) {
      element.dataset.mediaId = `auto-${element.tagName.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    }
    
    const id = element.dataset.mediaId;
    const type = element.tagName.toLowerCase();
    
    // Don't re-register if already managed by ID
    if (this.players.has(id)) return;
    
    this.registerPlayer(id, element, type);
    
    // Add event listeners for auto-managed elements
    const handlePlay = () => {
      this.requestPlay(id, true); // Always pause others for auto-detected elements
    };
    
    const handlePause = () => {
      this.pausePlayer(id);
    };
    
    element.addEventListener('play', handlePlay);
    element.addEventListener('pause', handlePause);
    
    // Store cleanup function
    element._mediaManagerCleanup = () => {
      element.removeEventListener('play', handlePlay);
      element.removeEventListener('pause', handlePause);
      this.unregisterPlayer(id);
    };
  }

  setupGlobalMediaListeners() {
    // Listen for the browser's native media events
    document.addEventListener('play', (e) => {
      const element = e.target;
      if (element.tagName === 'AUDIO' || element.tagName === 'VIDEO') {
        // Always try to auto-register (the function will skip if already managed)
        this.autoRegisterElement(element);
      }
    }, true);
  }

  registerPlayer(id, element, type = 'audio') {
    this.players.set(id, {
      element,
      type,
      isPlaying: false
    });
    console.log(`Registered player: ${id} (${type})`);
    this.emit('player-changed', this.getStatus());
  }

  unregisterPlayer(id) {
    this.players.delete(id);
    if (this.activePlayer === id) {
      this.activePlayer = null;
    }
    console.log(`Unregistered player: ${id}`);
    this.emit('player-changed', this.getStatus());
  }

  // Cleanup method for when manager is destroyed
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Cleanup all auto-registered elements
    this.players.forEach((player) => {
      if (player.element && player.element._mediaManagerCleanup) {
        player.element._mediaManagerCleanup();
      }
    });
    
    this.players.clear();
  }

  requestPlay(id, pauseOthers = true) {
    console.log(`Request play: ${id}, pauseOthers: ${pauseOthers}`);
    console.log(`Current players:`, this.getPlayerList());
    
    // Only pause other players if requested
    if (pauseOthers) {
      this.players.forEach((player, playerId) => {
        if (playerId !== id && player.isPlaying) {
          console.log(`Pausing other player: ${playerId}`);
          this.pausePlayer(playerId);
        }
      });
    }

    // Set as active player (or co-active if not pausing others)
    this.activePlayer = id;
    if (this.players.has(id)) {
      this.players.get(id).isPlaying = true;
    }

    // Emit event
    this.emit('player-changed', this.getStatus());
  }

  pausePlayer(id) {
    console.log(`Pause player: ${id}`);
    
    const player = this.players.get(id);
    if (player) {
      try {
        if (player.element && typeof player.element.pause === 'function') {
          player.element.pause();
        }
        player.isPlaying = false;
      } catch (error) {
        console.error(`Error pausing player ${id}:`, error);
      }
    }

    if (this.activePlayer === id) {
      this.activePlayer = null;
    }

    // Emit event
    this.emit('player-changed', this.getStatus());
  }

  pauseAll() {
    console.log('Pause all players');
    this.players.forEach((player, id) => {
      if (player.isPlaying) {
        this.pausePlayer(id);
      }
    });
  }

  getPlayerList() {
    return Array.from(this.players.entries()).map(([id, player]) => ({
      id,
      type: player.type,
      isPlaying: player.isPlaying
    }));
  }

  getStatus() {
    return {
      activePlayer: this.activePlayer,
      playerCount: this.players.size,
      players: this.getPlayerList()
    };
  }

  // Simple event emitter
  emit(event, data) {
    window.dispatchEvent(new CustomEvent(`media-manager-${event}`, { detail: data }));
  }

  on(event, callback) {
    window.addEventListener(`media-manager-${event}`, callback);
  }

  off(event, callback) {
    window.removeEventListener(`media-manager-${event}`, callback);
  }
}

// Create singleton instance
const mediaManager = new MediaManager();

// Expose globally
window.mediaManager = mediaManager;

export default mediaManager;
